import type { LatLon } from '@ev/shared'
import { useEffect } from 'react'
import { create } from 'zustand'

/**
 * The visitor's position, shared by everything that draws it.
 *
 * One store rather than one `useState` per caller, because there are now three
 * readers of the same fix - the blue dot on the map, the camera that frames it,
 * and the distance on the detail panel - and a fix they do not agree about
 * would show a dot in one place and a distance measured from another. The GPS
 * is polled once and the answer fans out.
 *
 * It sits beside the app store rather than inside it for the same reason the
 * overlay store does: the app store is defined as "only what the URL holds",
 * and a position is neither shareable nor something anybody would want written
 * into a link.
 *
 * `fixes` counts accepted answers. The map frames the location view when it
 * goes up, which is what separates "the visitor asked again" from "React
 * re-rendered" - the second fix of an unchanged position still means somebody
 * pressed the button and is waiting for the camera to move.
 */

export type LocationState = 'unsupported' | 'prompt' | 'locating' | 'granted' | 'denied' | 'failed'

interface LocationStore {
  readonly state: LocationState
  readonly position: LatLon | null
  readonly fixes: number
  request(): void
}

/** A fix older than this is stale enough to be worth refreshing. */
const MAX_AGE_MS = 5 * 60 * 1000

const TIMEOUT_MS = 10_000

export const useLocationStore = create<LocationStore>((set, get) => ({
  state: 'prompt',
  position: null,
  fixes: 0,

  request: () => {
    if (typeof navigator === 'undefined' || navigator.geolocation === undefined) {
      set({ state: 'unsupported' })
      return
    }
    // One outstanding poll at a time: a second press while the GPS is still
    // thinking would queue a second prompt behind the first.
    if (get().state === 'locating') return

    set({ state: 'locating' })
    navigator.geolocation.getCurrentPosition(
      (fix) =>
        set((current) => ({
          state: 'granted',
          position: { lat: fix.coords.latitude, lon: fix.coords.longitude },
          fixes: current.fixes + 1,
        })),
      (error) => set({ state: error.code === error.PERMISSION_DENIED ? 'denied' : 'failed' }),
      { maximumAge: MAX_AGE_MS, timeout: TIMEOUT_MS },
    )
  },
}))

/**
 * Ask for the position once per page load.
 *
 * The map opens on the visitor rather than on the whole country, so the fix is
 * what the first paint is waiting for and asking for it is not a surprise -
 * which is a change of policy from when this only put a distance on the detail
 * panel, and the reason the prompt now appears unbidden.
 *
 * Guarded at module scope, not by the effect's own cleanup: StrictMode mounts
 * twice and the filter sheet remounts the tree on a viewport change, and a
 * second `getCurrentPosition` behind a prompt the visitor has not answered yet
 * is how browsers end up queueing two dialogs.
 */
let requested = false

export const useLocationBootstrap = (): void => {
  const request = useLocationStore((store) => store.request)

  useEffect(() => {
    if (requested) return
    requested = true
    request()
  }, [request])
}

/** Test seam: lets a spec exercise the bootstrap more than once. */
export const resetLocationBootstrap = (): void => {
  requested = false
  useLocationStore.setState({ state: 'prompt', position: null, fixes: 0 })
}

/**
 * The shape the detail panel has always read. Kept so a caller that only wants
 * a distance does not have to know there is a store behind it.
 */
interface UserLocation {
  readonly state: LocationState
  readonly position: LatLon | null
  request(): void
}

export const useUserLocation = (): UserLocation => {
  const state = useLocationStore((store) => store.state)
  const position = useLocationStore((store) => store.position)
  const request = useLocationStore((store) => store.request)
  return { state, position, request }
}
